import { useFetcher, useSearchParams } from 'react-router';
import {
  mapSearchParamsToSearchRequest,
  QUERY_SEARCH_PARAM_SEARCHTERM
} from '~/features/catalog/mappers/catalogSearchQueryMapper';
import { searchCatalogItems } from '~/features/catalog/catalog.server';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBasePath } from '~/common/hooks/useBasePath';
import { getSelectedLocation } from '~/shell/utils/locationUtil.server';
import { optionalProtectedLoader } from '~/shell/utils/authUtil.server';
import { PgArea } from '~/common/types/area';
import { getSalesOrderSetFilter } from '~/shell/utils/salesOrdersetFilterUtil';

export const loader = optionalProtectedLoader(async ({ request, params, area, api, url }) => {
  const isPgArea = params.area === PgArea;

  const searchRequestDto = mapSearchParamsToSearchRequest(url.searchParams);

  if (isPgArea) {
    searchRequestDto.ccId = await getSelectedLocation(request);
  }

  searchRequestDto.searchInSalesOrderSet = await getSalesOrderSetFilter(request);

  const catalogItems = await searchCatalogItems(api, searchRequestDto, area === PgArea);
  return { catalogItems };
});

/**
 * Hook to search for catalog items if the searchTerm is in the url.
 * Uses the search parameters from the URL to search for matching catalog items.
 */
export function useCatalogSearch() {
  const { load, data, state } = useFetcher<typeof loader>();
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { path } = useBasePath();

  useEffect(() => {
    if (searchParams.has(QUERY_SEARCH_PARAM_SEARCHTERM)) {
      load(`${path}/resources/catalog/search?${searchParams.toString()}`);
    }
  }, [i18n.language, load, path, searchParams]);

  return {
    catalogItems: data?.catalogItems?.sort((a, b) => b.count - a.count) ?? [],
    isLoading: state === 'loading'
  };
}
